/**
 * TagsSettings — etiquetas.
 * Mobile: toolbar Contatos-like. Desktop intacto.
 */
import React, { useState, useMemo, useEffect } from 'react';
import { PageHeader } from '../../../components/layout/Header';
import TagTable from './components/TagTable';
import TagFormModal from './components/TagFormModal';
import ConfirmationModal from '../../../components/shared/ConfirmationModal';
import ActionMenu, { createImportAction, createExportAction } from '../../../components/shared/ActionMenu';
import SearchInput from '../../../components/ui/SearchInput';
import { Button } from '../../../components/ui/Button';
import { notify } from '../../../utils/toast';
import BulkActionsBar, { BulkAction } from '../../../components/shared/BulkActionsBar';
import { TrashIcon } from '../../../components/icons';
import { useTagsPage } from './hooks/useTagsPage';
import Pagination from '../../../components/shared/Pagination';

const TagsSettings: React.FC = () => {
    const {
        filteredTags,
        loading,
        selectedIds,
        searchTerm,
        isFormModalOpen,
        tagToEdit,
        isDeleteModalOpen,
        tagToDelete,
        isBulkDeleteModalOpen,
        setSearchTerm,
        setIsFormModalOpen,
        setIsDeleteModalOpen,
        setIsBulkDeleteModalOpen,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleOpenDeleteModal,
        handleSaveTag,
        handleConfirmDelete,
        handleBulkDelete,
        handleDuplicate,
    } = useTagsPage();

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;

    const paginatedTags = useMemo(() => {
        const firstPageIndex = (currentPage - 1) * pageSize;
        return filteredTags.slice(firstPageIndex, firstPageIndex + pageSize);
    }, [currentPage, filteredTags, pageSize]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const headerActions = [
        createImportAction(() => notify.info('Funcionalidade de importação em desenvolvimento')),
        createExportAction(() => notify.info('Funcionalidade de exportação em desenvolvimento')),
    ];

    const bulkActions: BulkAction[] = [
        {
            label: 'Excluir',
            icon: TrashIcon,
            onClick: () => setIsBulkDeleteModalOpen(true),
            variant: 'danger' as const,
        },
    ];

    return (
        <div className="flex h-full flex-col">
            <PageHeader
                title="Etiquetas"
                subtitle="Gerencie as etiquetas para categorizar negociações."
                className="mb-0 gap-s3 md:mb-6 md:gap-3"
                titleClassName="text-lg md:text-xl"
                actionClassName="w-full md:w-auto"
                hideBackButton
                action={
                    <>
                        <div className="flex w-full flex-col gap-s3 md:hidden">
                            <div
                                role="toolbar"
                                aria-label="Ações de etiquetas"
                                className="inline-flex h-11 w-fit items-stretch overflow-hidden rounded-xl border border-border bg-card"
                            >
                                <ActionMenu
                                    actions={headerActions}
                                    buttonClassName="h-11 w-11 rounded-none border-0 bg-transparent p-0 text-default shadow-none hover:bg-surface-subtle hover:text-primary"
                                />
                            </div>
                            <SearchInput
                                value={searchTerm}
                                onChange={setSearchTerm}
                                placeholder="Pesquisar etiquetas..."
                                className="min-h-11 w-full"
                            />
                            <Button
                                type="button"
                                onClick={handleOpenCreateModal}
                                className="min-h-11 w-full touch-manipulation"
                            >
                                Nova Etiqueta
                            </Button>
                        </div>
                        <div className="hidden md:flex md:items-center md:gap-2.5">
                            <div className="w-[250px] shrink-0">
                                <SearchInput
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    placeholder="Pesquisar etiquetas..."
                                    className="h-[36px]"
                                />
                            </div>
                            <Button type="button" onClick={handleOpenCreateModal}>
                                Nova Etiqueta
                            </Button>
                            <ActionMenu actions={headerActions} />
                        </div>
                    </>
                }
            />

            <section aria-label="Lista de etiquetas" className="overflow-x-auto pt-s3 md:pt-0">
                <TagTable
                    tags={paginatedTags}
                    isLoading={loading}
                    onEdit={handleOpenEditModal}
                    onDelete={handleOpenDeleteModal}
                    onDuplicate={handleDuplicate}
                    selectedIds={selectedIds}
                    onToggleSelection={toggleSelection}
                    onToggleAll={() => toggleAllSelection(filteredTags)}
                />
            </section>
            <Pagination
                className="mt-s3"
                currentPage={currentPage}
                totalCount={filteredTags.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                selectedCount={selectedIds.size}
            />

            <TagFormModal
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                onSave={handleSaveTag}
                tagToEdit={tagToEdit}
            />

            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Excluir Etiqueta"
                message={`Tem certeza que deseja excluir a etiqueta "${tagToDelete?.name}"?`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <ConfirmationModal
                isOpen={isBulkDeleteModalOpen}
                onClose={() => setIsBulkDeleteModalOpen(false)}
                onConfirm={handleBulkDelete}
                title="Excluir Etiquetas"
                message={`Tem certeza que deseja excluir ${selectedIds.size} etiqueta(s)? Esta ação não pode ser desfeita.`}
                confirmButtonText="Excluir"
                confirmButtonColor="red"
            />

            <BulkActionsBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClose={clearSelection}
            />
        </div>
    );
};

export default TagsSettings;
